/**
 * usageTeamService.js
 * ===================
 * Cliente do GET /api/me/usage/team (C3 visao EQUIPE — treinador).
 *
 * Retorna consumo agregado dos atletas do treinador logado no mes corrente,
 * com breakdown por atleta e por agente.
 *
 * Usado por:
 *   - Widget compacto em CoachHomePage
 *   - Pagina /meu-consumo/equipe (TeamUsagePage)
 *
 * Permissao: SO TREINADOR. Atleta/gestor/outros recebem 403.
 *
 * Cache em memoria: 60s (mesma estrategia do usageService).
 */
import { AZURE } from './api';

let _cache = null;
let _cacheTs = null;
const CACHE_TTL_MS = 60 * 1000;

/**
 * Busca o consumo agregado da equipe do treinador logado.
 *
 * @param {Object} opts
 * @param {boolean} opts.force - Se true, ignora cache.
 * @returns {Promise<{
 *   coach: {user_id, name, role, club_id},
 *   team:  {athletes_total, athletes_active},
 *   usage_current_month: {
 *     period_start, period_end,
 *     interactions, tokens, cost_usd,
 *     by_agent:   [{agent_name, interactions, tokens}, ...],
 *     by_athlete: [{athlete_user_id, athlete_name, interactions, tokens, cost_usd}, ...]
 *   }
 * } | null>}
 *
 * Retorna NULL silencioso em 403 (nao eh treinador) — o caller decide se
 * esconde o widget. Outros erros propagam.
 */
export async function getTeamUsage({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && _cacheTs && (now - _cacheTs) < CACHE_TTL_MS) {
    return _cache;
  }
  try {
    const { data } = await AZURE.get('/me/usage/team');
    if (!data?.success) {
      throw new Error(data?.error || 'Falha ao buscar /me/usage/team');
    }
    _cache = data.data;
    _cacheTs = now;
    return _cache;
  } catch (err) {
    // 403 = nao eh treinador -> silencioso (widget deve sumir)
    if (err?.response?.status === 403) {
      return null;
    }
    throw err;
  }
}

export function invalidateTeamUsageCache() {
  _cache = null;
  _cacheTs = null;
}
